import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { useAppDispatch } from '../store/hooks';
import { saveUserSession } from '../store/authSlice';
import AuthService from '../services/auth';

interface EmailLoginScreenProps {
  onBack: () => void;
}

const EmailLoginScreen: React.FC<EmailLoginScreenProps> = ({ onBack }) => {
  const dispatch = useAppDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);

  // 验证邮箱格式
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 验证密码强度
  const validatePassword = (password: string): boolean => {
    return password.length >= 6;
  };

  // 邮箱登录
  const handleSignIn = async () => {
    if (!validateEmail(email)) {
      Alert.alert('错误', '请输入有效的邮箱地址');
      return;
    }

    if (!validatePassword(password)) {
      Alert.alert('错误', '密码长度至少6位');
      return;
    }

    try {
      setLoading(true);
      const user = await AuthService.signInWithEmail(email, password);
      dispatch(saveUserSession(user));
      Alert.alert('成功', '登录成功！');
    } catch (error: any) {
      console.error('Email login error:', error);
      Alert.alert('错误', error.message || '登录失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 邮箱注册
  const handleSignUp = async () => {
    if (!validateEmail(email)) {
      Alert.alert('错误', '请输入有效的邮箱地址');
      return;
    }

    if (!validatePassword(password)) {
      Alert.alert('错误', '密码长度至少6位');
      return;
    }

    if (password !== confirmPassword) {
      Alert.alert('错误', '两次输入的密码不一致');
      return;
    }

    try {
      setLoading(true);
      await AuthService.signUpWithEmail(email, password);
      Alert.alert(
        '注册成功',
        '我们已向您的邮箱发送了验证邮件，请查收并点击验证链接完成注册。',
        [
          { text: '确定', onPress: () => setIsSignUp(false) },
        ]
      );
    } catch (error: any) {
      console.error('Email signup error:', error);
      Alert.alert('错误', error.message || '注册失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 忘记密码
  const handleForgotPassword = async () => {
    if (!validateEmail(email)) {
      Alert.alert('提示', '请先输入您的邮箱地址');
      return;
    }

    try {
      setLoading(true);
      await AuthService.resetPassword(email);
      Alert.alert('成功', '密码重置邮件已发送，请查收邮箱');
    } catch (error: any) {
      console.error('Reset password error:', error);
      Alert.alert('错误', error.message || '重置失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // Google 登录
  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      const user = await AuthService.signInWithGoogle();
      dispatch(saveUserSession(user));
      Alert.alert('成功', '登录成功！');
    } catch (error: any) {
      console.error('Google login error:', error);
      Alert.alert('错误', `Google登录失败：${error.message || '未知错误'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <TouchableOpacity style={styles.backButton} onPress={onBack}>
          <Text style={styles.backButtonText}>← 返回</Text>
        </TouchableOpacity>

        <Text style={styles.title}>
          {isSignUp ? '邮箱注册' : '邮箱登录'}
        </Text>
        <Text style={styles.subtitle}>
          {isSignUp ? '创建您的账户' : '使用邮箱和密码登录'}
        </Text>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>邮箱地址</Text>
          <TextInput
            style={[styles.input, !validateEmail(email) && email && styles.invalidInput]}
            placeholder="请输入邮箱地址"
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
          />
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>密码</Text>
          <TextInput
            style={[styles.input, !validatePassword(password) && password && styles.invalidInput]}
            placeholder="请输入密码（至少6位）"
            value={password}
            onChangeText={setPassword}
            secureTextEntry
            autoCapitalize="none"
          />
        </View>

        {isSignUp && (
          <View style={styles.inputContainer}>
            <Text style={styles.label}>确认密码</Text>
            <TextInput
              style={[styles.input, password !== confirmPassword && confirmPassword && styles.invalidInput]}
              placeholder="请再次输入密码"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry
              autoCapitalize="none"
            />
          </View>
        )}

        <TouchableOpacity
          style={[styles.button, loading && styles.buttonDisabled]}
          onPress={isSignUp ? handleSignUp : handleSignIn}
          disabled={loading}
        >
          <Text style={styles.buttonText}>
            {loading ? (isSignUp ? '注册中...' : '登录中...') : (isSignUp ? '注册' : '登录')}
          </Text>
        </TouchableOpacity>

        {!isSignUp && (
          <TouchableOpacity
            style={styles.forgotButton}
            onPress={handleForgotPassword}
            disabled={loading}
          >
            <Text style={styles.forgotButtonText}>忘记密码？</Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity
          style={styles.switchButton}
          onPress={() => {
            setIsSignUp(!isSignUp);
            setConfirmPassword('');
          }}
        >
          <Text style={styles.switchButtonText}>
            {isSignUp ? '已有账户？点击登录' : '没有账户？点击注册'}
          </Text>
        </TouchableOpacity>

        <View style={styles.divider}>
          <Text style={styles.dividerText}>其他登录方式</Text>
        </View>

        <TouchableOpacity
          style={[styles.socialButton, styles.googleButton]}
          onPress={handleGoogleSignIn}
          disabled={loading}
        >
          <Text style={styles.socialButtonText}>
            使用 Google 登录
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.socialButton, styles.phoneButton]}
          onPress={() => Alert.alert('提示', '手机号登录功能开发中')}
          disabled={loading}
        >
          <Text style={styles.socialButtonText}>
            使用手机号登录
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f0f0f0',
  },
  scrollContent: {
    flexGrow: 1,
    padding: 20,
    justifyContent: 'center',
  },
  backButton: {
    position: 'absolute',
    top: 50,
    left: 20,
    zIndex: 1,
  },
  backButtonText: {
    fontSize: 16,
    color: '#4285F4',
    fontWeight: '600',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 40,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    color: '#333',
    marginBottom: 8,
    fontWeight: '600',
  },
  input: {
    height: 50,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 15,
    fontSize: 16,
    backgroundColor: '#fff',
    color: '#333',
  },
  invalidInput: {
    borderColor: '#ff6b6b',
  },
  button: {
    backgroundColor: '#4285F4',
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  forgotButton: {
    alignItems: 'center',
    marginBottom: 20,
  },
  forgotButtonText: {
    color: '#4285F4',
    fontSize: 14,
    fontWeight: '600',
  },
  switchButton: {
    alignItems: 'center',
    marginTop: 20,
  },
  switchButtonText: {
    color: '#666',
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  divider: {
    marginVertical: 30,
    alignItems: 'center',
  },
  dividerText: {
    color: '#999',
    fontSize: 14,
    paddingHorizontal: 20,
    backgroundColor: '#f0f0f0',
  },
  socialButton: {
    height: 45,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
    borderWidth: 1,
  },
  googleButton: {
    backgroundColor: '#fff',
    borderColor: '#ddd',
  },
  phoneButton: {
    backgroundColor: '#fff',
    borderColor: '#ddd',
  },
  socialButtonText: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
});

export default EmailLoginScreen;
